// ─────────────────────────────────────────────────────────────────────────
// LawVo Workspace — version + changelog
//
// MAINTENANCE RULE
// Whenever the codebase changes, do BOTH of these in the same change:
//   1. Bump `VERSION` (semver: MAJOR.MINOR.PATCH).
//      - PATCH: bug fix, small UX tweak
//      - MINOR: new feature, non-breaking
//      - MAJOR: breaking change
//   2. Prepend a new entry to `CHANGELOG` at index 0 with today's date and
//      a short bullet list of user-visible changes (one bullet per change).
//
// Keep entries SHORT and user-facing — what the user will notice, not the
// implementation details. The changelog is rendered verbatim in the
// "What's new" modal on the Home page.
// ─────────────────────────────────────────────────────────────────────────

export const VERSION = '0.5.1'
export interface ChangelogEntry {
  version: string
  date: string // ISO YYYY-MM-DD
  changes: string[]
}
export const CHANGELOG: ChangelogEntry[] = [
  {
    version: '0.5.1',
    date: '2026-06-17',
    changes: [
      'Fixed a startup crash that could leave the app on a blank screen when API environment settings were read in certain preview environments.',
    ],
  },
  {
    version: '0.5.0',
    date: '2026-06-16',
    changes: [
      'JWT management now matches lawvo-client and doc-center: lawvo-api uses lawvo_state.accessToken, Strapi uses dcState.jwt.',
      'Translation state (glossary, segment progress, locks) persists on the Strapi backend instead of browser-only storage.',
      'Smart sync detects new, changed, and removed sections; approved locked sections survive source updates.',
      'Bold/italic no longer split sections; inline variables still split for safe merge.',
      'Sync from Server fetches the live English template to detect added, edited, and removed sections.',
      'Manual Merge Sections UI removed; section boundaries follow the English document structure.',
      'v1.0 backups still import; exports use v2.0 with full segment state.',
    ],
  },
  {
    version: '0.4.3',
    date: '2026-05-28',
    changes: [
      'Fixed a missing space after the period when building a translation by clicking multiple draft sentences — sentences are now joined into a properly spaced paragraph in both the editor and the synced document.',
    ],
  },
  {
    version: '0.4.2',
    date: '2026-05-28',
    changes: [
      'Sync now keeps the French variable blocks instead of falling back to their English versions, while still keeping bullets, headings and formatting aligned. (Falls back to the English structure only if the French document has diverged.)',
    ],
  },
  {
    version: '0.4.1',
    date: '2026-05-28',
    changes: [
      'Fixed French translations being cut off at the end of long paragraphs. The per-draft token budget was raised, and the workspace now flags any draft that was truncated by the model instead of silently approving it.',
    ],
  },
  {
    version: '0.4.0',
    date: '2026-05-28',
    changes: [
      'Added a version badge on the Home page with a "What\'s new" changelog.',
    ],
  },
  {
    version: '0.3.0',
    date: '2026-05-28',
    changes: [
      'Fixed formatting drift on "Sync to Document Hub" — bullets, headings, bold and italic runs now stay aligned with the source document, even when paragraphs contain inline variables.',
    ],
  },
  {
    version: '0.2.2',
    date: '2026-05-28',
    changes: [
      'Fixed numbered headings (e.g. "1. Purpose") that were showing only the number after translation — the full heading translation now flows through.',
    ],
  },
  {
    version: '0.2.1',
    date: '2026-05-28',
    changes: [
      'Added a "Re-translate" button on the Source English panel to discard an approved translation and regenerate fresh drafts.',
      'Console now logs the full request and response for each translation draft (system prompt, user prompt, raw model output, latency).',
    ],
  },
  {
    version: '0.2.0',
    date: '2026-05-28',
    changes: [
      'Improved heading translation: a heading like "1. Purpose" is now translated as one segment instead of being split into "1." and "Purpose".',
    ],
  },
  {
    version: '0.1.0',
    date: '2026-05-28',
    changes: [
      'Added a Preview Mode bypass on the login screen for off-domain environments (Magic Patterns, localhost) where the LawVo login API is unreachable due to CORS. Hidden on lawvo.com.',
    ],
  },
]
